
import {Component} from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import moment from 'moment';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {COL, sqlExtractYear, sqlExtractMonth, sqlSumByDocType, sqlSum, getTableName, buildNewCustomerFilter} from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {CustomerSupplierTradeCard, PermissionsGroup, ValueText} from '@/store/models.def';
import {GobiColor} from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        SecondaryLineBarChartFilterable,
        SelectFilter,
        DataInfo,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class SalesTrendLineChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public get yAxisLabel2() {
        // Line chart feature is disabled, no secondary axis needed
        return '';
    }

    public get compareYearList() {
        const currentYear = moment(this.selectedAsOfDate).year();

        const list: number[] = [];

        for (let i = 1; i < 30; i++) {
            const year = currentYear - i;
            list.push(year);
        }

        return list;
    }

    public get timeframe() {
        return [
            'Monthly',
            '3 months',
            '6 months',
        ];
    }

    public get columnList(): ValueText[] {
        const list = [
            {text: 'Sales Amt(All Customers)', value: 'allSalesAmount'},
            {text: 'Sales Amt(New Customers)', value: 'newSalesAmount'},
            {text: 'Sales Amt(Existing Customers)', value: 'existingSalesAmount'},
            {text: 'Sales Profit(All Customers)', value: 'allProfitAmount'},
            {text: 'Sales Profit(New Customers)', value: 'newProfitAmount'},
            {text: 'Sales Profit(Existing Customers)', value: 'existingProfitAmount'},
            {text: 'Credit Note Amt(All Customers)', value: 'allCreditNote'},
            {text: 'Number of New Customers', value: 'newCustomer'},
        ];

        return list;
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get currentMonth() {
        return moment(this.selectedAsOfDate).month();
    }

    public get computedYearRange(): [number, number] {
        return [
            moment(this.selectedDateRange[1])
                .add(-1, 'year')
                .add(1, 'month')
                .startOf('month').valueOf(),
            moment(this.selectedDateRange[1]).valueOf(),
        ];
    }

    public get compareYearRange(): [number, number] {
        return [
            moment(this.computedYearRange[0])
                .add(-1, 'year')
                .valueOf(),
            moment(this.computedYearRange[1])
                .add(-1, 'year')
                .endOf('month')
                .valueOf(),
        ];
    }

    public get yAxisLabel() {
        let label = '';

        if (this.selectedColumn !== 'newCustomer') {
            label = gdbx.currencySymbol;
        }

        return label;
    }

    public get decimal() {
        return gdbx.numDecimal;
    }

    public get columnName() {
        const found = this.columnList.find(
            (vt) => vt.value === this.selectedColumn,
        );
        let result = '';
        if (found) {
            result = found.text;
        }
        return result;
    }

    public get dataViewFormatter() {
        return DataViewFormatter.salesTracker;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    /**
     * Key for data fetching - only changes when filters change (not column/timeframe)
     */
    private get _dataFetchKey(): string {
        return JSON.stringify([
            this.computedYearRange,
            this.compareYearRange,
            this.selectedAgents,
            this.selectedCustomers,
            this.selectedDocProjects,
            this.selectedDTLProjects,
        ]);
    }

    public get expensiveHook() {
        const {
            currentYear,
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedCustomers,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
            selectedColumn,
            selectedTimeframe,
        } = this;
        return JSON.stringify([
            currentYear,
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedCustomers,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
            selectedColumn,
            selectedTimeframe,
        ]);
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public xAxisData: string[] = [];
    public series: echarts.ECharts[] | echarts.ECharts | any = [];

    public selectedColumn: string = this.columnList[0].value;
    public selectedComparedYear: number = this.compareYearList[0];
    public selectedTimeframe: string = this.timeframe[0];
    public yearEndTotal: string = '';
    public comparedYearEndTotal: string = '';

    public columnDatas: number[] = [];
    public comparedColumnDatas: number[] = [];

    // Cached raw data - loaded on demand based on selected column
    private _cachedAllData: {
        // SL data this year (all customers)
        slIvCsAmounts: number[];
        slDnAmounts: number[];
        slCnAmounts: number[];
        slIvCsProfits: number[];
        slDnProfits: number[];
        slCnProfits: number[];
        // AR data this year (all customers)
        arIvAmounts: number[];
        arCnAmounts: number[];
        arDnAmounts: number[];
        // SL data last year (all customers)
        slIvCsAmountsLY: number[];
        slDnAmountsLY: number[];
        slCnAmountsLY: number[];
        slIvCsProfitsLY: number[];
        slDnProfitsLY: number[];
        slCnProfitsLY: number[];
        // AR data last year (all customers)
        arIvAmountsLY: number[];
        arCnAmountsLY: number[];
        arDnAmountsLY: number[];
    } | null = null;
    private _cachedAllDataKey: string = '';

    // Cached new customer data - only loaded when needed
    private _cachedNewData: {
        // New customer data this year
        slNewIvCsAmounts: number[];
        slNewDnAmounts: number[];
        slNewCnAmounts: number[];
        slNewIvCsProfits: number[];
        slNewDnProfits: number[];
        slNewCnProfits: number[];
        arNewIvAmounts: number[];
        arNewCnAmounts: number[];
        arNewDnAmounts: number[];
        // New customer data last year
        slNewIvCsAmountsLY: number[];
        slNewDnAmountsLY: number[];
        slNewCnAmountsLY: number[];
        slNewIvCsProfitsLY: number[];
        slNewDnProfitsLY: number[];
        slNewCnProfitsLY: number[];
        arNewIvAmountsLY: number[];
        arNewCnAmountsLY: number[];
        arNewDnAmountsLY: number[];
    } | null = null;
    private _cachedNewDataKey: string = '';

    // Cached new customer counts (for newCustomer column)
    private _cachedNewCustomerCounts: {
        counts: number[];
        comparedCounts: number[];
    } | null = null;
    private _cachedNewCustomerCountsKey: string = '';

    /**
     * Applies 3-month or 6-month grouping to columnDatas and comparedColumnDatas.
     * Assumes input arrays are 12 monthly values in oldest-first order (index 0 = oldest month).
     * Output will also be in oldest-first order.
     * @param monthLabels - Array of 12 month labels (oldest to newest)
     */
    public applyTimeframeGrouping(monthLabels: Array<{ text: string }>) {
        if (this.selectedTimeframe === '3 months') {
            // Group 12 months into 4 quarters (oldest first)
            // Input: [Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec, Jan] (oldest first)
            // Output: [Feb-Apr, May-Jul, Aug-Oct, Nov-Jan] (oldest first)
            const groupedData: number[] = [];
            const groupedComparedData: number[] = [];

            for (let i = 0; i < 4; i++) {
                const startIdx = i * 3;
                groupedData.push(
                    this.columnDatas[startIdx] + this.columnDatas[startIdx + 1] + this.columnDatas[startIdx + 2],
                );
                groupedComparedData.push(
                    this.comparedColumnDatas[startIdx]
                    + this.comparedColumnDatas[startIdx + 1]
                    + this.comparedColumnDatas[startIdx + 2],
                );
            }

            this.columnDatas = groupedData;
            this.comparedColumnDatas = groupedComparedData;

            // Labels for oldest-first order: [0-2], [3-5], [6-8], [9-11]
            this.xAxisData = [
                `${monthLabels[0].text} - ${monthLabels[2].text}`,
                `${monthLabels[3].text} - ${monthLabels[5].text}`,
                `${monthLabels[6].text} - ${monthLabels[8].text}`,
                `${monthLabels[9].text} - ${monthLabels[11].text}`,
            ];
        } else if (this.selectedTimeframe === '6 months') {
            // Group 12 months into 2 halves (oldest first)
            const groupedData: number[] = [];
            const groupedComparedData: number[] = [];

            // First half: oldest 6 months (indices 0-5)
            groupedData.push(
                this.columnDatas[0] + this.columnDatas[1] + this.columnDatas[2] +
                this.columnDatas[3] + this.columnDatas[4] + this.columnDatas[5],
            );
            groupedComparedData.push(
                this.comparedColumnDatas[0] + this.comparedColumnDatas[1] + this.comparedColumnDatas[2] +
                this.comparedColumnDatas[3] + this.comparedColumnDatas[4] + this.comparedColumnDatas[5],
            );

            // Second half: newest 6 months (indices 6-11)
            groupedData.push(
                this.columnDatas[6] + this.columnDatas[7] + this.columnDatas[8] +
                this.columnDatas[9] + this.columnDatas[10] + this.columnDatas[11],
            );
            groupedComparedData.push(
                this.comparedColumnDatas[6] + this.comparedColumnDatas[7] + this.comparedColumnDatas[8] +
                this.comparedColumnDatas[9] + this.comparedColumnDatas[10] + this.comparedColumnDatas[11],
            );

            this.columnDatas = groupedData;
            this.comparedColumnDatas = groupedComparedData;

            // Labels for oldest-first order: [0-5], [6-11]
            this.xAxisData = [
                `${monthLabels[0].text} - ${monthLabels[5].text}`,
                `${monthLabels[6].text} - ${monthLabels[11].text}`,
            ];
        }
    }

    public thousandFormatter(x) {
        return Math.ceil(x).toLocaleString('en-US');
    }

    /**
     * Build month labels for x-axis (oldest-first, 12 months)
     */
    public buildMonthLabels(): string[] {
        const labels: string[] = [];
        const start = moment(this.computedYearRange[0]);
        for (let i = 0; i < 12; i++) {
            labels.push(start.clone().add(i, 'month').format('MMM YY'));
        }
        return labels;
    }

    public async expensiveCalc() {
        const jobId = (this.currentJobId = uuid.v4());
        this.$forceUpdate();
        this.generateLoadingText(0.2, 0.35);

        const col = this.selectedColumn;
        const currentFetchKey = this._dataFetchKey;

        // Determine what data we need based on selected column
        const newCustomerColumns = ['newSalesAmount', 'existingSalesAmount', 'newProfitAmount', 'existingProfitAmount'];
        const needsNewCustomerData = newCustomerColumns.includes(col);
        const needsNewCustomerCounts = col === 'newCustomer';

        // ========================================
        // FAST: Handle "newCustomer" column - SQL COUNT only
        // ========================================
        if (needsNewCustomerCounts) {
            const useCountsCache = this._cachedNewCustomerCounts !== null
                && this._cachedNewCustomerCountsKey === currentFetchKey;

            if (useCountsCache) {
                this.columnDatas = [...this._cachedNewCustomerCounts!.counts];
                this.comparedColumnDatas = [...this._cachedNewCustomerCounts!.comparedCounts];
            } else {
                this.generateLoadingText(0.35, 0.6);

                // Ensure globalCustomers table is loaded and get a head for queries
                const queryHead = await this._ensureTablesLoaded(['globalCustomers']);
                this.checkInterrupted(jobId);

                const globalCustomersTable = getTableName('globalCustomers');
                const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

                // Build agent/customer filters
                let agentFilter = '';
                if (this.selectedAgents.length > 0) {
                    const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
                    agentFilter = ` AND "AGENT" IN (${list})`;
                }

                let customerFilter = '';
                if (this.selectedCustomers.length > 0) {
                    const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
                    customerFilter = ` AND "CODE" IN (${list})`;
                }

                this.generateLoadingText(0.6, 0.9);

                // Single query for both year ranges
                const [countsByMonth, comparedCountsByMonth] = await Promise.all([
                    queryHead.rawQuery(`
                        SELECT
                            EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("FIRSTDEAL" AS BIGINT) + 28800000)) as year,
                            EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("FIRSTDEAL" AS BIGINT) + 28800000)) as month,
                            COUNT(*) as count
                        FROM ${globalCustomersTable}
                        WHERE TRY_CAST("FIRSTDEAL" AS BIGINT) BETWEEN ${this.computedYearRange[0]} AND ${this.computedYearRange[1]}
                            ${agentFilter}${customerFilter}
                        GROUP BY year, month
                        ORDER BY year, month
                    `),
                    queryHead.rawQuery(`
                        SELECT
                            EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST("FIRSTDEAL" AS BIGINT) + 28800000)) as year,
                            EXTRACT(MONTH FROM EPOCH_MS(TRY_CAST("FIRSTDEAL" AS BIGINT) + 28800000)) as month,
                            COUNT(*) as count
                        FROM ${globalCustomersTable}
                        WHERE TRY_CAST("FIRSTDEAL" AS BIGINT) BETWEEN ${this.compareYearRange[0]} AND ${this.compareYearRange[1]}
                            ${agentFilter}${customerFilter}
                        GROUP BY year, month
                        ORDER BY year, month
                    `),
                ]);
                this.checkInterrupted(jobId);
                this.generateLoadingText(0.95);

                this.columnDatas = this._alignToMonthArray(
                    countsByMonth.map((r) => ({
                        year: Number(r.year), month: Number(r.month), count: Number(r.count),
                    })),
                    this.computedYearRange,
                    (i: { count: number }) => i.count,
                );
                this.comparedColumnDatas = this._alignToMonthArray(
                    comparedCountsByMonth.map((r) => ({
                        year: Number(r.year), month: Number(r.month), count: Number(r.count),
                    })),
                    this.compareYearRange,
                    (i: { count: number }) => i.count,
                );

                // Store in cache
                this._cachedNewCustomerCounts = {
                    counts: [...this.columnDatas],
                    comparedCounts: [...this.comparedColumnDatas],
                };
                this._cachedNewCustomerCountsKey = currentFetchKey;
            }

            this._finishChart();
            return;
        }

        // ========================================
        // Load "all customers" data (always needed for non-newCustomer columns)
        // ========================================
        let slIvCsAmounts: number[];
        let slDnAmounts: number[];
        let slCnAmounts: number[];
        let slIvCsProfits: number[];
        let slDnProfits: number[];
        let slCnProfits: number[];
        let arIvAmounts: number[];
        let arCnAmounts: number[];
        let arDnAmounts: number[];
        let slIvCsAmountsLY: number[];
        let slDnAmountsLY: number[];
        let slCnAmountsLY: number[];
        let slIvCsProfitsLY: number[];
        let slDnProfitsLY: number[];
        let slCnProfitsLY: number[];
        let arIvAmountsLY: number[];
        let arCnAmountsLY: number[];
        let arDnAmountsLY: number[];

        const useAllCache = this._cachedAllData !== null && this._cachedAllDataKey === currentFetchKey;

        if (useAllCache) {
            // Use cached "all customers" data
            slIvCsAmounts = this._cachedAllData!.slIvCsAmounts;
            slDnAmounts = this._cachedAllData!.slDnAmounts;
            slCnAmounts = this._cachedAllData!.slCnAmounts;
            slIvCsProfits = this._cachedAllData!.slIvCsProfits;
            slDnProfits = this._cachedAllData!.slDnProfits;
            slCnProfits = this._cachedAllData!.slCnProfits;
            arIvAmounts = this._cachedAllData!.arIvAmounts;
            arCnAmounts = this._cachedAllData!.arCnAmounts;
            arDnAmounts = this._cachedAllData!.arDnAmounts;
            slIvCsAmountsLY = this._cachedAllData!.slIvCsAmountsLY;
            slDnAmountsLY = this._cachedAllData!.slDnAmountsLY;
            slCnAmountsLY = this._cachedAllData!.slCnAmountsLY;
            slIvCsProfitsLY = this._cachedAllData!.slIvCsProfitsLY;
            slDnProfitsLY = this._cachedAllData!.slDnProfitsLY;
            slCnProfitsLY = this._cachedAllData!.slCnProfitsLY;
            arIvAmountsLY = this._cachedAllData!.arIvAmountsLY;
            arCnAmountsLY = this._cachedAllData!.arCnAmountsLY;
            arDnAmountsLY = this._cachedAllData!.arDnAmountsLY;
        } else {
            // Fetch "all customers" data - 4 queries total
            this.generateLoadingText(0.35, 0.5);

            // Ensure all required tables are loaded before running queries
            const queryHead = await this._ensureTablesLoaded(['sales', 'creditNotes', 'customerTrades', 'globalCustomers']);
            this.checkInterrupted(jobId);

            this.generateLoadingText(0.5, 0.85);

            // Load all customers data in parallel (4 queries)
            const [slDataThisYear, customerTradesThisYear, slDataLastYear, customerTradesLastYear] = await Promise.all([
                this._loadSLDataByMonth(queryHead, this.computedYearRange),
                this._loadCustomerTradesDataByMonth(queryHead, this.computedYearRange),
                this._loadSLDataByMonth(queryHead, this.compareYearRange),
                this._loadCustomerTradesDataByMonth(queryHead, this.compareYearRange),
            ]);
            this.checkInterrupted(jobId);

            this.generateLoadingText(0.9);

            // Convert to 12-element arrays
            const align = <T>(
                data: Array<{ year: number; month: number } & T>,
                range: [number, number],
                fn: (i: T) => number,
            ) => this._alignToMonthArray(data, range, fn);

            slIvCsAmounts = align(slDataThisYear, this.computedYearRange, (i) => i.ivCsAmount);
            slDnAmounts = align(slDataThisYear, this.computedYearRange, (i) => i.dnAmount);
            slCnAmounts = align(slDataThisYear, this.computedYearRange, (i) => i.cnAmount);
            slIvCsProfits = align(slDataThisYear, this.computedYearRange, (i) => i.ivCsProfit);
            slDnProfits = align(slDataThisYear, this.computedYearRange, (i) => i.dnProfit);
            slCnProfits = align(slDataThisYear, this.computedYearRange, (i) => i.cnProfit);

            arIvAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.ivAmount);
            arCnAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.cnAmount);
            arDnAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.dnAmount);

            slIvCsAmountsLY = align(slDataLastYear, this.compareYearRange, (i) => i.ivCsAmount);
            slDnAmountsLY = align(slDataLastYear, this.compareYearRange, (i) => i.dnAmount);
            slCnAmountsLY = align(slDataLastYear, this.compareYearRange, (i) => i.cnAmount);
            slIvCsProfitsLY = align(slDataLastYear, this.compareYearRange, (i) => i.ivCsProfit);
            slDnProfitsLY = align(slDataLastYear, this.compareYearRange, (i) => i.dnProfit);
            slCnProfitsLY = align(slDataLastYear, this.compareYearRange, (i) => i.cnProfit);

            arIvAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.ivAmount);
            arCnAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.cnAmount);
            arDnAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.dnAmount);

            // Cache the data
            this._cachedAllData = {
                slIvCsAmounts,
                slDnAmounts,
                slCnAmounts,
                slIvCsProfits,
                slDnProfits,
                slCnProfits,
                arIvAmounts,
                arCnAmounts,
                arDnAmounts,
                slIvCsAmountsLY,
                slDnAmountsLY,
                slCnAmountsLY,
                slIvCsProfitsLY,
                slDnProfitsLY,
                slCnProfitsLY,
                arIvAmountsLY,
                arCnAmountsLY,
                arDnAmountsLY,
            };
            this._cachedAllDataKey = currentFetchKey;
        }

        // ========================================
        // Load "new customers" data only if needed
        // ========================================
        let slNewIvCsAmounts: number[] = Array(12).fill(0);
        let slNewDnAmounts: number[] = Array(12).fill(0);
        let slNewCnAmounts: number[] = Array(12).fill(0);
        let slNewIvCsProfits: number[] = Array(12).fill(0);
        let slNewDnProfits: number[] = Array(12).fill(0);
        let slNewCnProfits: number[] = Array(12).fill(0);
        let arNewIvAmounts: number[] = Array(12).fill(0);
        let arNewCnAmounts: number[] = Array(12).fill(0);
        let arNewDnAmounts: number[] = Array(12).fill(0);
        let slNewIvCsAmountsLY: number[] = Array(12).fill(0);
        let slNewDnAmountsLY: number[] = Array(12).fill(0);
        let slNewCnAmountsLY: number[] = Array(12).fill(0);
        let slNewIvCsProfitsLY: number[] = Array(12).fill(0);
        let slNewDnProfitsLY: number[] = Array(12).fill(0);
        let slNewCnProfitsLY: number[] = Array(12).fill(0);
        let arNewIvAmountsLY: number[] = Array(12).fill(0);
        let arNewCnAmountsLY: number[] = Array(12).fill(0);
        let arNewDnAmountsLY: number[] = Array(12).fill(0);

        if (needsNewCustomerData) {
            const useNewCache = this._cachedNewData !== null && this._cachedNewDataKey === currentFetchKey;

            if (useNewCache) {
                // Use cached "new customers" data
                slNewIvCsAmounts = this._cachedNewData!.slNewIvCsAmounts;
                slNewDnAmounts = this._cachedNewData!.slNewDnAmounts;
                slNewCnAmounts = this._cachedNewData!.slNewCnAmounts;
                slNewIvCsProfits = this._cachedNewData!.slNewIvCsProfits;
                slNewDnProfits = this._cachedNewData!.slNewDnProfits;
                slNewCnProfits = this._cachedNewData!.slNewCnProfits;
                arNewIvAmounts = this._cachedNewData!.arNewIvAmounts;
                arNewCnAmounts = this._cachedNewData!.arNewCnAmounts;
                arNewDnAmounts = this._cachedNewData!.arNewDnAmounts;
                slNewIvCsAmountsLY = this._cachedNewData!.slNewIvCsAmountsLY;
                slNewDnAmountsLY = this._cachedNewData!.slNewDnAmountsLY;
                slNewCnAmountsLY = this._cachedNewData!.slNewCnAmountsLY;
                slNewIvCsProfitsLY = this._cachedNewData!.slNewIvCsProfitsLY;
                slNewDnProfitsLY = this._cachedNewData!.slNewDnProfitsLY;
                slNewCnProfitsLY = this._cachedNewData!.slNewCnProfitsLY;
                arNewIvAmountsLY = this._cachedNewData!.arNewIvAmountsLY;
                arNewCnAmountsLY = this._cachedNewData!.arNewCnAmountsLY;
                arNewDnAmountsLY = this._cachedNewData!.arNewDnAmountsLY;
            } else {
                // Fetch "new customers" data - 4 more queries
                this.generateLoadingText(0.9, 0.95);

                // Tables should already be loaded from "all customers" fetch, but ensure they are
                const tablesToLoad: Array<
                    'sales' | 'creditNotes' | 'customerTrades' | 'globalCustomers'
                > = ['sales', 'creditNotes', 'customerTrades', 'globalCustomers'];
                const queryHead = await this._ensureTablesLoaded(tablesToLoad);

                const [
                    slDataNewThisYear,
                    customerTradesNewThisYear,
                    slDataNewLastYear,
                    customerTradesNewLastYear,
                ] = await Promise.all([
                    this._loadSLDataByMonth(queryHead, this.computedYearRange, this.computedYearRange),
                    this._loadCustomerTradesDataByMonth(queryHead, this.computedYearRange, this.computedYearRange),
                    this._loadSLDataByMonth(queryHead, this.compareYearRange, this.compareYearRange),
                    this._loadCustomerTradesDataByMonth(queryHead, this.compareYearRange, this.compareYearRange),
                ]);
                this.checkInterrupted(jobId);

                const align = <T>(
                    data: Array<{ year: number; month: number } & T>,
                    range: [number, number],
                    fn: (i: T) => number,
                ) => this._alignToMonthArray(data, range, fn);

                slNewIvCsAmounts = align(slDataNewThisYear, this.computedYearRange, (i) => i.ivCsAmount);
                slNewDnAmounts = align(slDataNewThisYear, this.computedYearRange, (i) => i.dnAmount);
                slNewCnAmounts = align(slDataNewThisYear, this.computedYearRange, (i) => i.cnAmount);
                slNewIvCsProfits = align(slDataNewThisYear, this.computedYearRange, (i) => i.ivCsProfit);
                slNewDnProfits = align(slDataNewThisYear, this.computedYearRange, (i) => i.dnProfit);
                slNewCnProfits = align(slDataNewThisYear, this.computedYearRange, (i) => i.cnProfit);

                arNewIvAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.ivAmount);
                arNewCnAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.cnAmount);
                arNewDnAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.dnAmount);

                slNewIvCsAmountsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.ivCsAmount);
                slNewDnAmountsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.dnAmount);
                slNewCnAmountsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.cnAmount);
                slNewIvCsProfitsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.ivCsProfit);
                slNewDnProfitsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.dnProfit);
                slNewCnProfitsLY = align(slDataNewLastYear, this.compareYearRange, (i) => i.cnProfit);

                arNewIvAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.ivAmount);
                arNewCnAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.cnAmount);
                arNewDnAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.dnAmount);

                // Cache the data
                this._cachedNewData = {
                    slNewIvCsAmounts,
                    slNewDnAmounts,
                    slNewCnAmounts,
                    slNewIvCsProfits,
                    slNewDnProfits,
                    slNewCnProfits,
                    arNewIvAmounts,
                    arNewCnAmounts,
                    arNewDnAmounts,
                    slNewIvCsAmountsLY,
                    slNewDnAmountsLY,
                    slNewCnAmountsLY,
                    slNewIvCsProfitsLY,
                    slNewDnProfitsLY,
                    slNewCnProfitsLY,
                    arNewIvAmountsLY,
                    arNewCnAmountsLY,
                    arNewDnAmountsLY,
                };
                this._cachedNewDataKey = currentFetchKey;
            }
        }

        // ========================================
        // Calculate amounts based on toggle settings
        // ========================================
        const calcAmount = (iv: number[], cn: number[], dn: number[]) =>
            iv.map((v, i) => v - (this.includeCreditNote ? cn[i] : 0) + (this.includeDebitNote ? dn[i] : 0));

        // All customers amounts/profits
        const slAllAmounts = calcAmount(slIvCsAmounts, slCnAmounts, slDnAmounts);
        const slAllAmountsLY = calcAmount(slIvCsAmountsLY, slCnAmountsLY, slDnAmountsLY);
        const arAllAmounts = calcAmount(arIvAmounts, arCnAmounts, arDnAmounts);
        const arAllAmountsLY = calcAmount(arIvAmountsLY, arCnAmountsLY, arDnAmountsLY);

        const slAllProfits = calcAmount(slIvCsProfits, slCnProfits, slDnProfits);
        const slAllProfitsLY = calcAmount(slIvCsProfitsLY, slCnProfitsLY, slDnProfitsLY);
        const arAllProfits = slAllProfits.map((p, i) => p + (arAllAmounts[i] - slAllAmounts[i]));
        const arAllProfitsLY = slAllProfitsLY.map((p, i) => p + (arAllAmountsLY[i] - slAllAmountsLY[i]));

        // Select based on SL/AR toggle
        const allAmounts = this.slPhModuleOnly ? slAllAmounts : arAllAmounts;
        const allAmountsLY = this.slPhModuleOnly ? slAllAmountsLY : arAllAmountsLY;
        const allProfits = this.slPhModuleOnly ? slAllProfits : arAllProfits;
        const allProfitsLY = this.slPhModuleOnly ? slAllProfitsLY : arAllProfitsLY;
        const cnAmounts = this.slPhModuleOnly ? slCnAmounts : arCnAmounts;
        const cnAmountsLY = this.slPhModuleOnly ? slCnAmountsLY : arCnAmountsLY;

        // New customers amounts/profits (only calculate if needed)
        let newAmounts: number[] = [];
        let newAmountsLY: number[] = [];
        let newProfits: number[] = [];
        let newProfitsLY: number[] = [];

        if (needsNewCustomerData) {
            const slNewAmounts = calcAmount(slNewIvCsAmounts, slNewCnAmounts, slNewDnAmounts);
            const slNewAmountsLY = calcAmount(slNewIvCsAmountsLY, slNewCnAmountsLY, slNewDnAmountsLY);
            const arNewAmounts = calcAmount(arNewIvAmounts, arNewCnAmounts, arNewDnAmounts);
            const arNewAmountsLY = calcAmount(arNewIvAmountsLY, arNewCnAmountsLY, arNewDnAmountsLY);

            const slNewProfits = calcAmount(slNewIvCsProfits, slNewCnProfits, slNewDnProfits);
            const slNewProfitsLY = calcAmount(slNewIvCsProfitsLY, slNewCnProfitsLY, slNewDnProfitsLY);
            const arNewProfits = slNewProfits.map((p, i) => p + (arNewAmounts[i] - slNewAmounts[i]));
            const arNewProfitsLY = slNewProfitsLY.map((p, i) => p + (arNewAmountsLY[i] - slNewAmountsLY[i]));

            newAmounts = this.slPhModuleOnly ? slNewAmounts : arNewAmounts;
            newAmountsLY = this.slPhModuleOnly ? slNewAmountsLY : arNewAmountsLY;
            newProfits = this.slPhModuleOnly ? slNewProfits : arNewProfits;
            newProfitsLY = this.slPhModuleOnly ? slNewProfitsLY : arNewProfitsLY;
        }

        // ========================================
        // Set data based on selected column
        // ========================================
        switch (col) {
            case 'allSalesAmount':
                this.columnDatas = allAmounts;
                this.comparedColumnDatas = allAmountsLY;
                break;
            case 'allCreditNote':
                this.columnDatas = cnAmounts;
                this.comparedColumnDatas = cnAmountsLY;
                break;
            case 'allProfitAmount':
                this.columnDatas = allProfits;
                this.comparedColumnDatas = allProfitsLY;
                break;
            case 'newSalesAmount':
                this.columnDatas = newAmounts;
                this.comparedColumnDatas = newAmountsLY;
                break;
            case 'existingSalesAmount':
                this.columnDatas = allAmounts.map((v, i) => v - newAmounts[i]);
                this.comparedColumnDatas = allAmountsLY.map((v, i) => v - newAmountsLY[i]);
                break;
            case 'newProfitAmount':
                this.columnDatas = newProfits;
                this.comparedColumnDatas = newProfitsLY;
                break;
            case 'existingProfitAmount':
                this.columnDatas = allProfits.map((v, i) => v - newProfits[i]);
                this.comparedColumnDatas = allProfitsLY.map((v, i) => v - newProfitsLY[i]);
                break;
        }

        this._finishChart();
    }

    public onHovering(value: boolean, filterId: string) {
        this.$emit('hovering', value, filterId);
    }

    public onEditing(value: boolean, filterId: string) {
        this.$emit('editing', value, filterId);
    }

    /**
     * Common chart finalization: labels, timeframe grouping, series, totals
     */
    private _finishChart() {
        this.generateLoadingText(1);

        // Build x-axis labels
        this.xAxisData = this.buildMonthLabels();

        // Apply timeframe grouping if needed
        if (this.selectedTimeframe !== 'Monthly') {
            const monthLabels = this.xAxisData.map((t) => ({ text: t }));
            this.applyTimeframeGrouping(monthLabels);
        }

        this.series = [
            {
                name: this.columnName + ' This Year',
                type: 'bar',
                barWidth: '30%',
                itemStyle: { color: GobiColor.COLOR1 },
                data: this.columnDatas,
            },
            {
                name: this.columnName + ' Last Year',
                type: 'bar',
                barWidth: '30%',
                itemStyle: { color: GobiColor.COLOR3 },
                data: this.comparedColumnDatas,
            },
        ];

        this.yearEndTotal = gdbx.currencySymbol + ' '
            + this.thousandFormatter(this.columnDatas.reduce((a, b) => a + b, 0));
        this.comparedYearEndTotal = gdbx.currencySymbol + ' '
            + this.thousandFormatter(this.comparedColumnDatas.reduce((a, b) => a + b, 0));

        this.saveHistory('series', 'xAxisData', 'yearEndTotal', 'comparedYearEndTotal');
    }

    /**
     * Build base filter clause for manual SQL queries
     */
    private _buildBaseFilter(dateRange: [number, number]): string {
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        let filter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}`;

        if (this.selectedAgents.length > 0) {
            const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
            filter += ` AND ${COL.AGENT} IN (${list})`;
        }

        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            filter += ` AND "DOCPROJECT" IN (${list})`;
        }

        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            filter += ` AND "DTLPROJECT" IN (${list})`;
        }

        return filter;
    }

    /**
     * Build customer filter clause for new/existing customers
     */
    private _buildCustomerFilter(newCustomerDateRange?: [number, number]): string {
        if (newCustomerDateRange) {
            return buildNewCustomerFilter({
                dateRange: newCustomerDateRange,
                agents: this.selectedAgents,
                customers: this.selectedCustomers,
            });
        } else if (this.selectedCustomers.length > 0) {
            const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
            const customerList = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
            return ` AND ${COL.CODE} IN (${customerList})`;
        }
        return '';
    }

    /**
     * Load SL data (sales + creditNotes) for a date range using a single UNION ALL query.
     * Returns ivCsAmount, dnAmount, cnAmount, ivCsProfit, dnProfit, cnProfit per month.
     * @param queryHead - Pre-fetched head for executing queries
     * @param newCustomerDateRange - If provided, filters to customers whose firstDeal is in this range
     */
    private async _loadSLDataByMonth(
        queryHead: any,
        dateRange: [number, number],
        newCustomerDateRange?: [number, number],
    ): Promise<Array<{
        year: number; month: number;
        ivCsAmount: number; dnAmount: number; cnAmount: number;
        ivCsProfit: number; dnProfit: number; cnProfit: number;
    }>> {
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const baseFilter = this._buildBaseFilter(dateRange);
        const customerFilter = this._buildCustomerFilter(newCustomerDateRange);

        const results = await queryHead.rawQuery(`
            SELECT
                ${sqlExtractYear('"docDate"')},
                ${sqlExtractMonth('"docDate"')},
                SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amount,
                SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN amount ELSE 0 END) as dn_amount,
                SUM(CASE WHEN src = 'cn' THEN amount ELSE 0 END) as cn_amount,
                SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN profit ELSE 0 END) as iv_cs_profit,
                SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN profit ELSE 0 END) as dn_profit,
                SUM(CASE WHEN src = 'cn' THEN profit ELSE 0 END) as cn_profit
            FROM (
                SELECT 'sales' as src, ${COL.DOCTYPE} as docType,
                    TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                    TRY_CAST(${COL.PROFIT} AS DOUBLE) as profit,
                    ${COL.DOCDATE} as "docDate"
                FROM ${salesTable}
                WHERE ${baseFilter}${customerFilter}
                UNION ALL
                SELECT 'cn' as src, 'CN' as docType,
                    TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                    TRY_CAST(${COL.PROFIT} AS DOUBLE) as profit,
                    ${COL.DOCDATE} as "docDate"
                FROM ${cnTable}
                WHERE ${baseFilter}${customerFilter}
            ) AS combined
            GROUP BY year, month
            ORDER BY year, month
        `);

        return results.map((r) => ({
            year: Number(r.year),
            month: Number(r.month),
            ivCsAmount: Number(r.iv_cs_amount) || 0,
            dnAmount: Number(r.dn_amount) || 0,
            cnAmount: Number(r.cn_amount) || 0,
            ivCsProfit: Number(r.iv_cs_profit) || 0,
            dnProfit: Number(r.dn_profit) || 0,
            cnProfit: Number(r.cn_profit) || 0,
        }));
    }

    /**
     * Load customerTrades data for a date range using a single rawQuery with CASE WHEN.
     * Returns ivAmount, cnAmount, dnAmount per month.
     * @param queryHead - Pre-fetched head for executing queries
     * @param newCustomerDateRange - If provided, filters to customers whose firstDeal is in this range
     */
    private async _loadCustomerTradesDataByMonth(
        queryHead: any,
        dateRange: [number, number],
        newCustomerDateRange?: [number, number],
    ): Promise<Array<{
        year: number; month: number;
        ivAmount: number; cnAmount: number; dnAmount: number;
    }>> {
        const arTable = getTableName('customerTrades');
        const baseFilter = this._buildBaseFilter(dateRange);
        const customerFilter = this._buildCustomerFilter(newCustomerDateRange);

        const results = await queryHead.rawQuery(`
            SELECT
                ${sqlExtractYear(COL.DOCDATE)},
                ${sqlExtractMonth(COL.DOCDATE)},
                ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
            FROM ${arTable}
            WHERE ${baseFilter}${customerFilter}
            GROUP BY year, month
            ORDER BY year, month
        `);

        return results.map((r) => ({
            year: Number(r.year),
            month: Number(r.month),
            ivAmount: Number(r.iv_amount) || 0,
            cnAmount: Number(r.cn_amount) || 0,
            dnAmount: Number(r.dn_amount) || 0,
        }));
    }

    /**
     * Convert raw month data to 12-element array aligned to computedYearRange
     */
    private _alignToMonthArray<T>(
        data: Array<{ year: number; month?: number } & T>,
        dateRange: [number, number],
        getValue: (item: T) => number,
    ): number[] {
        const result = Array(12).fill(0);
        const startMonth = moment(dateRange[0]).month() + 1; // 1-based
        const startYear = moment(dateRange[0]).year();

        for (const item of data) {
            const month = item.month || 1;
            const pos = (item.year - startYear) * 12 + (month - startMonth);
            if (pos >= 0 && pos < 12) {
                result[pos] = getValue(item as any);
            }
        }
        return result;
    }
}
